// Core React and routing imports
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { useAuth0 } from '@auth0/auth0-react'; // Added Auth0 import
import CircleMetaTags from '../../components/CircleMetaTags/CircleMetaTags';
import ShareCircleButton from '../../components/ShareCircleButton/ShareCircleButton';
import { trackSawNumber, trackLead } from '../../utils/analytics';
import axios from 'axios';

// Individual MUI imports for better tree-shaking
import Skeleton from '@mui/material/Skeleton';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Tooltip from '@mui/material/Tooltip';

// Lucide icons imports
import {
  Wifi,
  ArrowRight,
  Smartphone,
  Music,
  Info,
  Globe,
  MessageCircle,
  Check,
  AlertTriangle,
  BarChart2,
  BookmarkPlus,
  BookmarkMinus,
  ChevronUp,
  ChevronDown,
  ChevronRight,
  Calendar,
  DollarSign,
  Star,
  TrendingUp,
  Users,
  Shield,
  Gift,
  Headphones,
  CheckCircle,
  Tv,
  Search,
  Zap,
  Heart,
  X,
  Filter,
  Plus,
  Minus,
  TrendingDown,
  RefreshCw,
  SwitchCamera,
  Watch,
  Wallet,
  Phone,
  Percent,
  Sparkles
} from 'lucide-react';

import './PlanDetail.css';

// Debug mode flag
const DEBUG_MODE = true;

const iconComponents = {
  Wifi,
  Smartphone,
  Music,
  Globe,
  MessageCircle,
  Shield,
  Tv,
  Gift,
  Headphones,
  Info,
  Users,
  Check,
  AlertTriangle,
  BarChart2,
  BookmarkPlus,
  BookmarkMinus,
  ChevronUp,
  ChevronDown,
  ChevronRight,
  Calendar,
  DollarSign,
  Star,
  TrendingUp,
  Search,
  Zap,
  Heart,
  X,
  Filter,
  Plus,
  Minus,
  Sparkles,
  CheckCircle
};

// Updated carrier themes with modern gradients and colors
const carrierThemes = {
  'Verizon': {
    primary: '#FF0044',
    secondary: '#C4004D',
    gradient: 'linear-gradient(135deg, #FF0044 0%, #FF5E94 100%)',
    accent: '#FF4775',
    iconColor: '#FF0044',
    cardGradient: 'linear-gradient(to right, rgba(255, 0, 68, 0.15), rgba(255, 94, 148, 0.05))'
  },
  'AT&T': {
    primary: '#0057B8',
    secondary: '#0064D2',
    gradient: 'linear-gradient(135deg, #0057B8 0%, #00A9FF 100%)',
    accent: '#0064D2',
    iconColor: '#0057B8',
    cardGradient: 'linear-gradient(to right, rgba(0, 87, 184, 0.15), rgba(0, 169, 255, 0.05))'
  },
  'T-Mobile': {
    primary: '#E20074',
    secondary: '#7F0F3F',
    gradient: 'linear-gradient(135deg, #E20074 0%, #FF5EAD 100%)',
    accent: '#B30062',
    iconColor: '#E20074',
    cardGradient: 'linear-gradient(to right, rgba(226, 0, 116, 0.15), rgba(255, 94, 173, 0.05))'
  },
  'Cricket': {
    primary: '#4BB748',
    secondary: '#2D6E2C',
    gradient: 'linear-gradient(135deg, #4BB748 0%, #85E884 100%)',
    accent: '#3A923A',
    iconColor: '#4BB748',
    cardGradient: 'linear-gradient(to right, rgba(75, 183, 72, 0.15), rgba(133, 232, 132, 0.05))'
  }
};

// Enhanced Spatial Context-Aware Action Plane with consistent line adjusters
const SpatialContextActionPlane = ({
  planName,
  pricePerLine,
  activePlanFeatures = 4,
  totalFeatures = 8,
  carrierTheme,
  onReserve,
  confidence = 80,
  lines = 1,
  onLinesChange,
  availableSlots = 10,
  pricing,
  currentMembers = 0,
  familySize = 0
}) => {
  const [mode, setMode] = useState('standard');
  const { scrollY } = useScroll();
  const { theme } = React.useContext(ThemeContext); // Get current theme from context

  // Updated opacity transformation with smart fade
  const opacity = useTransform(
    scrollY,
    [0, 700, 800, 1400, 1500],
    [1, 1, 0, 0, 1]
  );

  const translateY = useTransform(
    scrollY,
    [0, 300, 700, 800, 1400, 1500],
    [0, 0, 0, 10, 10, 0]
  );

  // Always show line adjuster regardless of scroll position
  const [showLineAdjuster, setShowLineAdjuster] = useState(true);
  const [animatePrice, setAnimatePrice] = useState(false);

  // Calculate the appropriate price based on lines
  const calculatePrice = () => {
    if (!pricing || !pricing.pricing_tiers) return pricePerLine;

    // Calculate total members including existing members
    const currentTotal = currentMembers + familySize;
    const totalMembers = currentTotal + lines;

    const tiers = [...pricing.pricing_tiers].sort((a, b) => a.line_count - b.line_count);
    const matchingTier = tiers.find(tier => tier.line_count >= totalMembers);

    return matchingTier ? matchingTier.price_per_line : tiers[tiers.length - 1]?.price_per_line || pricePerLine;
  };

  const [displayPrice, setDisplayPrice] = useState(calculatePrice());

  // Update display price when lines change
  useEffect(() => {
    const newPrice = calculatePrice();
    if (newPrice !== displayPrice) {
      setAnimatePrice(true);
      setDisplayPrice(newPrice);

      setTimeout(() => {
        setAnimatePrice(false);
      }, 600);
    }
  }, [lines, pricing, currentMembers, familySize]);

  // Updated scroll behavior logic - Just update the mode, always show line adjuster
  useEffect(() => {
    const scrollListener = scrollY.onChange(latest => {
      // Standard to expanded transition
      if (latest > 300 && mode === 'standard') {
        setMode('expanded');
      }

      // Expanded to decision transition
      if (latest > 600 && mode === 'expanded') {
        setMode('decision');
      }

      // Reset to standard when scrolling back to top
      if (latest < 300 && mode !== 'standard') {
        setMode('standard');
      }
    });

    return () => scrollListener();
  }, [scrollY, mode]);

  // Updated message based on context
  const getPlanMessage = () => {
    // When in Your Plan Breakdown section
    if (scrollY.get() > 800 && scrollY.get() < 1400) {
      return "Reserve";
    }

    // When at the Select Your Plan section
    if (scrollY.get() < 800) {
      return "Reserve your plan";
    }

    // Default for other sections
    return "Reserve";
  };

  const isMaxLinesReached = lines >= availableSlots;
  const isMinLinesReached = lines <= 1;

  // Updated: Invert theme styles based on current theme
  const getInvertedThemeStyles = () => {
    // Invert styles based on current theme
    if (theme === 'dark') {
      // When site is in dark mode, footer should be light mode
      return {
        background: 'rgba(255, 255, 255, 0.95)',
        border: '1px solid rgba(255, 255, 255, 0.7)',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(255, 255, 255, 0.2), 0 0 0 1px rgba(255, 0, 68, 0.1)',
        color: '#111827'
      };
    } else {
      // When site is in light mode, footer should be dark mode
      return {
        background: 'rgba(26, 32, 44, 0.95)',
        border: '1px solid rgba(51, 65, 85, 0.7)',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(51, 65, 85, 0.7), 0 0 0 1px rgba(255, 0, 68, 0.1)',
        color: '#f8fafc'
      };
    }
  };

  // Updated: Define text colors based on inverted theme
  const textColor = theme === 'dark' ? '#111827' : '#f8fafc';
  const secondaryTextColor = theme === 'dark' ? '#374151' : '#cbd5e1';

  // Get styles for line adjuster
  const getLineAdjusterStyle = () => {
    return {
      backgroundColor: theme === 'dark' ? 'rgba(0, 0, 0, 0.1)' : 'rgba(0, 0, 0, 0.2)',
      border: theme === 'dark' ? '1px solid rgba(0, 0, 0, 0.1)' : '1px solid rgba(255, 255, 255, 0.1)'
    };
  };

  const invertedStyles = getInvertedThemeStyles();

  return (
    <motion.div
      className="ap-container"
      style={{
        opacity,
        y: translateY
      }}
    >
      <div className="ap-content" style={invertedStyles}>
        {/* Background effects */}
        <div className="ap-background-effects">
          <motion.div
            className="ap-glow-effect"
            animate={{
              opacity: [0.2, 0.6, 0.2],
              scale: [0.8, 1.2, 0.8]
            }}
            transition={{
              repeat: Infinity,
              duration: 6,
              ease: "easeInOut"
            }}
          />
        </div>

        <div className="ap-flex-container" style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center', // Changed from center to flex-start
          position: 'relative',
          zIndex: 2,
          gap: '16px'
        }}>
          {/* Left side - plan info */}
          <div className="ap-info-container" style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start', // Ensure left alignment
            flex: 1
          }}>
            {/* Larger plan name in upper left */}
            <div className="ap-plan-name" style={{
              color: textColor,
              fontWeight: '700',
              fontSize: '18px', // Increased from 16px
              marginBottom: '6px',
              textAlign: 'left'
            }}>
              {planName || "Unlimited Welcome"}
            </div>

            {/* Price container */}
            <div className="ap-plan-price-container" style={{
              display: 'flex',
              alignItems: 'baseline'
            }}>
              <motion.span
                className="ap-plan-price"
                key={displayPrice}
                animate={{
                  scale: animatePrice ? [1, 1.1, 1] : 1
                }}
                transition={{
                  duration: 0.4,
                  ease: "easeInOut"
                }}
                style={{
                  color: '#ff0044',
                  fontSize: '32px',
                  fontWeight: '700',
                  lineHeight: 1
                }}
              >
                ${displayPrice}
              </motion.span>
              <span className="ap-plan-price-unit" style={{
                color: secondaryTextColor,
                marginLeft: '4px'
              }}>
                /line per month
              </span>
            </div>

            {/* Total line display */}
            <div className="ap-line-count-display" style={{
              color: textColor,
              marginTop: '4px',
              fontSize: '14px'
            }}>
              <span>Total: ${(displayPrice * lines).toFixed(2)}/mo for {lines} {lines === 1 ? 'line' : 'lines'}</span>
            </div>

            {/* Removed features info & confidence indicator are still removed */}
          </div>

          {/* Middle section - line adjuster (always shown) */}
          <motion.div
            className="ap-line-adjuster"
            style={{
              display: 'flex',
              alignItems: 'center',
              ...getLineAdjusterStyle(),
              borderRadius: '30px',
              padding: '4px 8px'
            }}
          >
            <motion.button
              className="ap-line-button"
              onClick={() => onLinesChange(Math.max(1, lines - 1))}
              whileHover={{ scale: isMinLinesReached ? 1 : 1.1 }}
              whileTap={{ scale: isMinLinesReached ? 1 : 0.9 }}
              disabled={isMinLinesReached}
              style={{
                width: '24px',
                height: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                border: 'none',
                background: '#ff0044',
                color: 'white',
                cursor: isMinLinesReached ? 'not-allowed' : 'pointer',
                opacity: isMinLinesReached ? 0.5 : 1
              }}
            >
              <Minus size={14} />
            </motion.button>

            <motion.span
              className="ap-line-count"
              key={lines}
              style={{
                color: textColor,
                margin: '0 10px',
                fontWeight: '600',
                fontSize: '16px'
              }}
            >
              {lines}
            </motion.span>

            <motion.button
              className="ap-line-button"
              onClick={() => onLinesChange(Math.min(availableSlots, lines + 1))}
              whileHover={{ scale: isMaxLinesReached ? 1 : 1.1 }}
              whileTap={{ scale: isMaxLinesReached ? 1 : 0.9 }}
              disabled={isMaxLinesReached}
              style={{
                width: '24px',
                height: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                border: 'none',
                background: '#ff0044',
                color: 'white',
                cursor: isMaxLinesReached ? 'not-allowed' : 'pointer',
                opacity: isMaxLinesReached ? 0.5 : 1
              }}
            >
              <Plus size={14} />
            </motion.button>
          </motion.div>

          {/* Right side - action button */}
          <motion.button
            className="ap-action-button"
            whileHover={{
              scale: 1.05,
              boxShadow: '0 10px 25px rgba(255, 0, 68, 0.3)'
            }}
            whileTap={{ scale: 0.98 }}
            onClick={onReserve}
            style={{
              background: 'linear-gradient(135deg, #FF0044 0%, #FF5E94 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '50px',
              padding: '12px 24px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              whiteSpace: 'nowrap',
              boxShadow: '0 4px 10px rgba(255, 0, 68, 0.25)'
            }}
          >
            <span>{getPlanMessage()}</span>
            <ArrowRight size={18} />
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
};

// Theme Context: PlanDetail is light-only (B-007). Its dark theme was half built, so the
// text went light while backgrounds stayed light. The saved 'theme' value and the OS
// preference are ignored.
const LIGHT_THEME = { theme: 'light' };
const ThemeContext = React.createContext(LIGHT_THEME);

export const ThemeProvider = ({ children }) => {
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', 'light');
  }, []);

  return (
    <ThemeContext.Provider value={LIGHT_THEME}>
      {children}
    </ThemeContext.Provider>
  );
};

const PricingProgressBar = ({ pricing, currentTier, spotsLeft }) => {
  const tiers = pricing?.pricing_tiers || [];

  // Sort tiers by line_count to ensure proper ordering
  const sortedTiers = [...tiers].sort((a, b) => a.line_count - b.line_count);

  // Calculate total current members (family size + joiners)
  const totalCurrentMembers = currentTier?.line_count || 0;

  // Find the appropriate current tier and next tier
  const findCurrentAndNextTier = () => {
    if (sortedTiers.length === 0) {
      return { currentTierIndex: -1, currentTierData: null, nextTier: null };
    }

    // If total members is less than or equal to the first tier
    if (totalCurrentMembers <= sortedTiers[0].line_count) {
      return {
        currentTierIndex: 0,
        currentTierData: sortedTiers[0],
        nextTier: sortedTiers[1] || null
      };
    }

    // If total members exceeds the highest tier, use the highest tier
    if (totalCurrentMembers >= sortedTiers[sortedTiers.length - 1].line_count) {
      return {
        currentTierIndex: sortedTiers.length - 1,
        currentTierData: sortedTiers[sortedTiers.length - 1],
        nextTier: null // No next tier available
      };
    }

    // Find the appropriate tier for current members
    // We want the tier that this number of members qualifies for
    let currentTierIndex = -1;
    for (let i = 0; i < sortedTiers.length; i++) {
      if (totalCurrentMembers <= sortedTiers[i].line_count) {
        currentTierIndex = i;
        break;
      }
    }

    const currentTierData = sortedTiers[currentTierIndex];
    const nextTier = sortedTiers[currentTierIndex + 1] || null;

    return { currentTierIndex, currentTierData, nextTier };
  };

  const { currentTierIndex, currentTierData, nextTier } = findCurrentAndNextTier();

  // Calculate progress percentage
  const progressPercentage = currentTierIndex >= 0 && sortedTiers.length > 1
    ? (currentTierIndex / (sortedTiers.length - 1)) * 100
    : 0;

  console.log('PricingProgressBar Debug:', {
    totalCurrentMembers,
    sortedTiers: sortedTiers.map(t => ({ line_count: t.line_count, price: t.price_per_line })),
    currentTierIndex,
    currentTierData,
    nextTier,
    progressPercentage
  });

  return (
    <div className="pd-members-progress">
      <div className="pd-progress-section">
        <div className="pd-progress-container">
          {/* Base progress bar */}
          <div className="pd-progress-bar" />

          {/* Solid fill animates first */}
          <motion.div
            className="pd-progress-fill"
            initial={{ width: 0 }}
            animate={{ width: `${progressPercentage}%` }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          />

          {/* Dashed line animates after solid fill - only show if there's a next tier */}
          {nextTier && currentTierIndex >= 0 && (
            <motion.div
              className="pd-progress-dashed"
              initial={{ width: 0, opacity: 0 }}
              animate={{
                width: `${((currentTierIndex + 1) / (sortedTiers.length - 1) * 100) - progressPercentage}%`,
                opacity: 1
              }}
              style={{
                left: `${progressPercentage}%`
              }}
              transition={{
                width: { delay: 0.8, duration: 0.6, ease: "easeOut" },
                opacity: { delay: 0.8, duration: 0.3 }
              }}
            />
          )}

          {/* Price markers */}
          {sortedTiers.map((tier, index) => {
            const isActive = currentTierData && tier.line_count === currentTierData.line_count;
            const isNext = nextTier && tier.line_count === nextTier.line_count;

            return (
              <div
                key={tier.line_count}
                className={`pd-progress-marker ${isActive ? 'active' : ''} ${isNext ? 'next' : ''}`}
                style={{ left: `${(index / (sortedTiers.length - 1)) * 100}%` }}
              >
                {isNext && (
                  <motion.div
                    className="pd-future-price"
                    initial={false}
                    animate={{
                      opacity: 1,
                      y: 0,
                      x: 0
                    }}
                    transition={{
                      type: "spring",
                      stiffness: 100,
                      damping: 15,
                      mass: 0.8
                    }}
                  >
                    Group price when you join
                  </motion.div>
                )}

                <span className="pd-marker-price" style={{ color: isActive || isNext ? 'var(--primary-accent)' : 'inherit' }}>
                  ${tier.price_per_line}
                </span>
                <div className="pd-marker-dot" />
                {tier.savings_percentage > 0 && (
                  <span className="pd-marker-label">
                    Save {tier.savings_percentage}%
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="pd-message-section">
        <NextTierMessage
          currentTier={currentTierData}
          nextTier={nextTier}
          spotsLeft={spotsLeft}
          totalCurrentMembers={totalCurrentMembers}
        />
      </div>
    </div>
  );
};

const NextTierMessage = ({ currentTier, nextTier, spotsLeft, totalCurrentMembers }) => {
  const [messageIndex, setMessageIndex] = useState(0);

  const calculateMessages = () => {
    // If there's no next tier, show "best price achieved" messages
    if (!nextTier) {
      return [
        {
          text: `You're at the best group pricing tier!`,
          savings: `$${currentTier?.price_per_line || 0}/line`,
          isBestTier: true
        },
        {
          text: `Maximum savings achieved with ${totalCurrentMembers} members!`,
          savings: `Best Rate`,
          isBestTier: true
        },
        {
          text: `You've unlocked the lowest possible rate!`,
          savings: `$${currentTier?.price_per_line || 0}/line`,
          isBestTier: true
        }
      ];
    }

    // If there is a next tier, show standard progression messages
    const memberDifference = nextTier.line_count - totalCurrentMembers;
    const savingsPerLine = currentTier.price_per_line - nextTier.price_per_line;
    const totalSavings = savingsPerLine * nextTier.line_count;

    return [
      {
        text: `Add ${memberDifference} more member${memberDifference > 1 ? 's' : ''} to save $${savingsPerLine} per line!`,
        savings: `$${nextTier.price_per_line}/line`,
        isBestTier: false
      },
      {
        text: `Join now to save $${totalSavings} total per month!`,
        savings: `$${nextTier.price_per_line}/line`,
        isBestTier: false
      },
      {
        text: `Lower your bill to $${nextTier.price_per_line}/line with ${memberDifference} more member${memberDifference > 1 ? 's' : ''}!`,
        savings: `Save $${savingsPerLine}/line`,
        isBestTier: false
      }
    ];
  };

  useEffect(() => {
    const messages = calculateMessages();
    if (messages.length > 0) {
      const timer = setInterval(() => {
        setMessageIndex((prev) => (prev + 1) % messages.length);
      }, 4000);
      return () => clearInterval(timer);
    }
  }, [currentTier, nextTier, totalCurrentMembers]);

  const messages = calculateMessages();
  if (!messages.length) return null;
  const message = messages[messageIndex];

  return (
    <motion.div
      className="pd-next-tier"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="pd-next-tier-content">
        <div className="pd-next-tier-main">
          <motion.div
            className="pd-next-tier-icon"
            animate={{
              rotate: message.isBestTier ? [0, 10, 0] : [0, -10, 0],
              scale: [1, 1.1, 1]
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            {message.isBestTier ? (
              <Star size={16} />
            ) : (
              <TrendingDown size={16} />
            )}
          </motion.div>
          <AnimatePresence mode="wait">
            <motion.span
              key={message.text}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
            >
              {message.text}
            </motion.span>
          </AnimatePresence>
        </div>
        <motion.div
          className="pd-next-price-preview"
          animate={{
            backgroundColor: message.isBestTier 
              ? ["rgba(34, 197, 94, 0.1)", "rgba(34, 197, 94, 0.2)", "rgba(34, 197, 94, 0.1)"]
              : ["rgba(255, 255, 255, 0.1)", "rgba(255, 255, 255, 0.2)", "rgba(255, 255, 255, 0.1)"]
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >
          {message.isBestTier ? (
            <CheckCircle size={16} />
          ) : (
            <ArrowRight size={16} />
          )}
          <span>{message.savings}</span>
        </motion.div>
      </div>
      
      {/* Show different footer message based on tier status */}
      {!message.isBestTier && spotsLeft > 0 && (
        <motion.div
          className="pd-next-tier-spots"
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.8 }}
          transition={{ delay: 0.3 }}
        >
          Only {spotsLeft} spots to get the lowest rate!
        </motion.div>
      )}
      
      {message.isBestTier && (
        <motion.div
          className="pd-next-tier-spots"
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.8 }}
          transition={{ delay: 0.3 }}
          style={{ color: 'var(--success-color)' }}
        >
          🎉 You've reached maximum savings!
        </motion.div>
      )}
    </motion.div>
  );
};

// In the CircleInfo component, update the totalExistingMembers calculation:
const CircleInfo = ({ circle, carrier, plan, shareButton, carrierTheme }) => {
  if (!circle || !plan?.pricing) return null;

  // Calculate total existing members (total_joiners + family_size)
  const totalExistingMembers = (circle.total_joiners || 0) + (circle.family_size || 0);

  // Sort tiers to ensure proper ordering
  const sortedTiers = [...plan.pricing.pricing_tiers].sort((a, b) => a.line_count - b.line_count);

  // Find the appropriate pricing tier based on total members
  const findCurrentTierData = () => {
    if (sortedTiers.length === 0) return null;

    // If total members is less than or equal to the first tier
    if (totalExistingMembers <= sortedTiers[0].line_count) {
      return sortedTiers[0];
    }

    // If total members exceeds the highest tier, use the highest tier
    if (totalExistingMembers >= sortedTiers[sortedTiers.length - 1].line_count) {
      return sortedTiers[sortedTiers.length - 1];
    }

    // Find the appropriate tier for current members
    for (let i = 0; i < sortedTiers.length; i++) {
      if (totalExistingMembers <= sortedTiers[i].line_count) {
        return sortedTiers[i];
      }
    }

    // Fallback to the highest tier
    return sortedTiers[sortedTiers.length - 1];
  };

  const currentTierData = findCurrentTierData();

  console.log('CircleInfo Debug:', {
    totalJoiners: circle.total_joiners,
    familySize: circle.family_size,
    totalExistingMembers,
    sortedTiers: sortedTiers.map(t => ({ line_count: t.line_count, price: t.price_per_line })),
    currentTierData
  });

  return (
    <div className="pd-circle-info">
      <div className="pd-circle-header">
        <div className="pd-circle-title-group">
          <div className="pd-circle-icon-wrapper"
            style={{
              background: carrierTheme?.gradient || 'linear-gradient(135deg, #FF0044 0%, #FF5E94 100%)'
            }}
          >
            <Users size={24} color="#fff" />
          </div>
          <div className="pd-circle-title-content">
            <h2 className="pd-circle-name">{circle.name}</h2>
            <div className="pd-circle-subtitle">
              <span>{carrier.name} Plan</span>
              {circle.status === 'Verified' && (
                <span className="pd-verified-badge">
                  <CheckCircle className="pd-verified-icon" size={14} />
                </span>
              )}
            </div>
          </div>
        </div>

        {shareButton}
      </div>

      <PricingProgressBar
        pricing={plan.pricing}
        currentTier={{
          line_count: totalExistingMembers,
          price_per_line: currentTierData?.price_per_line || 0
        }}
        spotsLeft={circle.available_slots}
      />
    </div>
  );
};

// Simplified FeatureCard with self-contained tooltip
const FeatureCard = ({ feature, carrierTheme, compact = false, isActive = false }) => {
  const IconComponent = feature.icon ? iconComponents[feature.icon] : Info;
  const [showTooltip, setShowTooltip] = useState(false);

  // Simple toggle for tooltip visibility
  const handleMouseEnter = () => setShowTooltip(true);
  const handleMouseLeave = () => setShowTooltip(false);

  return (
    <motion.div
      className={`pd-feature-card ${compact ? 'pd-feature-card-compact' : ''} ${isActive ? 'pd-feature-card-active' : ''}`}
      whileHover={{
        scale: 1.03,
        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.12), 0 5px 10px rgba(0, 0, 0, 0.06)'
      }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        type: "spring",
        stiffness: 300,
        duration: 0.5
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{
        background: `var(--card-bg)`,
        backgroundImage: carrierTheme?.cardGradient || 'none',
        position: 'relative',
        cursor: 'pointer'
      }}
    >
      <div className="pd-feature-card-content">
        <div className={`pd-feature-icon-wrapper ${compact ? 'pd-feature-icon-compact' : ''}`}
          style={{ background: `${carrierTheme?.primary}20` || 'rgba(255, 0, 68, 0.2)' }}>
          <IconComponent size={compact ? 20 : 24} className="pd-feature-icon" style={{ color: carrierTheme?.primary || 'var(--primary-accent)' }} />
        </div>
        <div className="pd-feature-text">
          <h3 className={`pd-feature-title ${compact ? 'pd-feature-title-compact' : ''}`}>
            {feature.title}
          </h3>
          <p className={`pd-feature-description ${compact ? 'pd-feature-description-compact' : ''}`}>
            {feature.short_description}
          </p>
        </div>
      </div>

      {isActive && (
        <motion.div
          className="pd-feature-highlight-badge"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 500, damping: 15 }}
        >
          <Sparkles size={14} /> Key Feature
        </motion.div>
      )}

      {/* Self-contained tooltip that appears over the card */}
      {showTooltip && (feature.full_description || feature.short_description) && (
        <div
          className="pd-card-tooltip"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(30, 41, 59, 0.95)',
            color: '#ffffff',
            padding: '16px',
            borderRadius: '12px',
            zIndex: 10,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.25)',
            
            
            border: '1px solid rgba(255, 255, 255, 0.1)',
            transition: 'opacity 0.2s ease',
            opacity: 1
          }}
        >
          <h4 style={{
            margin: '0 0 8px 0',
            fontSize: '1rem',
            fontWeight: 600,
            color: '#ffffff'
          }}>
            {feature.title}
          </h4>
          <p style={{
            margin: 0,
            fontSize: '0.9rem',
            lineHeight: 1.5,
            color: '#f1f5f9'
          }}>
            {feature.full_description || feature.short_description}
          </p>
        </div>
      )}
    </motion.div>
  );
};

const ModernPlanToggleButton = ({ plan, isActive, onClick, carrierTheme }) => (
  <motion.div
    className={`pd-modern-toggle-button ${isActive ? 'active' : ''}`}
    layoutId={`plan-button-${plan.id}`}
    onClick={() => onClick(plan.name)}
    whileHover={{
      scale: 1.02,
      transition: { duration: 0.2 }
    }}
    whileTap={{ scale: 0.98 }}
    style={{
      ...(isActive && {
        borderColor: 'transparent',
        color: '#ffffff'
      })
    }}
  >
    <div className="pd-toggle-content" style={{ textAlign: 'left' }}>
      <h3 style={{ color: isActive ? '#ffffff' : 'var(--text-primary)' }}>
        {plan.name}
      </h3>
      <p style={{ color: isActive ? '#ffffff' : 'var(--text-secondary)' }}>
        {plan.description}
      </p>
    </div>

    {isActive && (
      <motion.div
        className="pd-toggle-check"
        initial={{ scale: 0, rotate: -90 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ type: "spring", stiffness: 500, damping: 15 }}
      >
        <Check size={18} color="#ffffff" />
      </motion.div>
    )}

    <div
      className="pd-toggle-highlight"
      style={{
        opacity: isActive ? 1 : 0,
        background: isActive ? carrierTheme.gradient : 'transparent'
      }}
    ></div>
  </motion.div>
);

// UPDATED: UnifiedPlanView component with T-Mobile restriction logic
const UnifiedPlanView = ({ plans, activePlan, onPlanChange, features, carrierTheme, carrier }) => {
  const [animateChange, setAnimateChange] = useState(false);

  // Find the currently selected plan
  const selectedPlan = plans.find(p => p.name === activePlan);

  // Check if carrier allows mix-and-match plans
  const allowsMixAndMatch = carrier?.name !== 'T-Mobile';

  // Determine which features to highlight
  const getHighlightStatus = (feature, index) => {
    // Simple logic: highlight first feature and alternate features based on selected plan
    if (index === 0) return true;
    if (selectedPlan?.id === 'welcome' && index === 2) return true;
    if (selectedPlan?.id === 'ultimate' && index === 3) return true;
    if (selectedPlan?.id === 'plus' && index === 1) return true;
    return false;
  };

  // Handle plan change with animation (only for non-T-Mobile carriers)
  const handlePlanChange = (planName) => {
    if (!allowsMixAndMatch) return; // Prevent plan changes for T-Mobile
    
    setAnimateChange(true);
    setTimeout(() => {
      onPlanChange(planName);
      setAnimateChange(false);
    }, 300);
  };

  return (
    <div className="pd-unified-plan-view">
      <div className="pd-unified-plan-container">
        {/* Left Side - Plan Selection or Current Plan Display */}
        <div className="pd-unified-plans-section">
          {allowsMixAndMatch ? (
            // Show plan selection for Verizon, AT&T, etc.
            <>
              <h2 className="pd-section-title">Select Your Plan</h2>
              <div className="pd-modern-toggle-buttons">
                <AnimatePresence>
                  {plans.map(plan => (
                    <ModernPlanToggleButton
                      key={plan.id}
                      plan={plan}
                      isActive={activePlan === plan.name}
                      onClick={handlePlanChange}
                      carrierTheme={carrierTheme}
                    />
                  ))}
                </AnimatePresence>
              </div>
            </>
          ) : (
            // Show current plan only for T-Mobile
            <>
              <h2 className="pd-section-title">Your Plan</h2>
              <div className="pd-tmobile-current-plan">
                <motion.div
                  className="pd-current-plan-display"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  style={{
                    padding: '24px',
                    borderRadius: '16px',
                    background: carrierTheme.gradient,
                    color: '#ffffff',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  {/* Background accent effect */}
                  <div style={{
                    position: 'absolute',
                    width: '150px',
                    height: '150px',
                    background: 'radial-gradient(circle, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0) 70%)',
                    borderRadius: '50%',
                    top: '-75px',
                    right: '-75px',
                    pointerEvents: 'none'
                  }}></div>

                  <div style={{ position: 'relative', zIndex: 1 }}>
                    <h3 style={{ margin: '0 0 8px 0', fontSize: '1.5rem', fontWeight: '700' }}>
                      {selectedPlan?.name}
                    </h3>
                    <p style={{ margin: '0 0 16px 0', opacity: 0.9 }}>
                      {selectedPlan?.description}
                    </p>
                    
                    {/* T-Mobile specific notice */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      marginTop: '16px'
                    }}>
                      <Info size={16} />
                      <span style={{ fontSize: '0.9rem' }}>
                        T-Mobile requires all family members to be on the same plan
                      </span>
                    </div>
                  </div>
                </motion.div>
              </div>
            </>
          )}
        </div>

        {/* Right Side - Features Display */}
        <div className="pd-unified-features-section">
          <motion.h3
            className="pd-features-subtitle"
            layoutId="features-title"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            key={activePlan}
          >
            <span className="pd-features-subtitle-text">Premium Features</span>
            <motion.div
              className="pd-animated-underline"
              layoutId="title-underline"
            ></motion.div>
          </motion.h3>

          <motion.div
            className={`pd-features-grid-compact ${animateChange ? 'changing' : ''}`}
            layout
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={activePlan}
                className="pd-features-grid-inner"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                {features.map((feature, index) => (
                  <FeatureCard
                    key={`${activePlan}-${feature.title}`}
                    feature={feature}
                    carrierTheme={carrierTheme}
                    compact={true}
                    isActive={getHighlightStatus(feature, index)}
                  />
                ))}
              </motion.div>
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

const PlanOfferItem = ({ icon: Icon, text, disabled = false, carrierTheme = {} }) => {
  // Default color values if carrierTheme is undefined
  const defaultColors = {
    iconColor: '#FF4775',  // Default pink color
    primary: '#FF4775'
  };

  // Use carrier theme colors or fallback to defaults
  const colors = {
    iconColor: carrierTheme?.iconColor || defaultColors.iconColor,
    primary: carrierTheme?.primary || defaultColors.primary
  };

  return (
    <motion.div
      className={`pd-plan-offer-item ${disabled ? 'disabled' : ''}`}
      whileHover={!disabled ? { scale: 1.02, backgroundColor: 'var(--hover-overlay)' } : {}}
    >
      <div className="pd-offer-icon-wrapper"
        style={{ backgroundColor: disabled ? 'var(--disabled-bg)' : `${colors.primary}20` }}>
        <Icon
          size={20}
          color={disabled ? 'var(--text-tertiary)' : colors.iconColor}
        />
      </div>
      <p>{text}</p>
    </motion.div>
  );
};

const WhatThisPlanOffers = ({ circle, carrierTheme }) => {
  const offers = [
    {
      icon: Calendar,
      text: "Access to online portal/My Verizon App",
      enabled: true
    },
    {
      icon: Smartphone,
      text: "eSIM allowed",
      enabled: true
    },
    {
      icon: Users,
      text: "Access to book appointments with a Customer Success Representative",
      enabled: true
    },
    {
      icon: Gift,
      text: "Shared perks between members (Disney+, Hulu, ESPN)",
      enabled: circle?.shared_perks
    },
    {
      icon: Percent,
      text: "Additional discounts applied (Teacher, Military, Healthcare Worker)",
      enabled: circle?.additional_discount
    },
    {
      icon: Watch,
      text: "Tablet & Watches allowed",
      enabled: true
    },
    {
      icon: Wallet,
      text: "Activation fee waived",
      enabled: circle?.activation_fee_waived
    },
    {
      icon: Phone,
      text: "Phone upgrades/financing",
      enabled: circle?.allow_phone_upgrade
    }
  ];

  return (
    <div className="pd-plan-offers">
      <h3 className="pd-section-title">What this plan offers</h3>
      <div className="pd-plan-offers-grid">
        {offers.map((offer, index) => (
          <PlanOfferItem
            key={index}
            icon={offer.icon}
            text={offer.text}
            disabled={!offer.enabled}
            carrierTheme={carrierTheme}
          />
        ))}
      </div>
    </div>
  );
};

// SavingsAndBenefits component properly integrated with existing pricing structure
const SavingsAndBenefits = ({ costs, carrierTheme, currentPlan, lines = 1 }) => {
  // Standard Verizon promotion amount - could be passed as a prop or included in costs
  const verizonPromotion = 550;

  // Access the pricing tiers directly from currentPlan
  const tiers = currentPlan?.pricing?.pricing_tiers || [];

  // Sort the tiers by line_count to ensure they're in order (lowest to highest)
  const sortedTiers = [...tiers].sort((a, b) => a.line_count - b.line_count);

  // For debugging purposes
  console.log('Sorted tiers:', sortedTiers);

  // Get standard price (highest price per line) - first element in original tiers array 
  // This should be equivalent to currentPlan?.pricing?.pricing_tiers?.[0]?.price_per_line
  const standardPrice = tiers.length > 0 ? tiers[0].price_per_line : 55;

  // Get lowest tier price (lowest price per line) - last element in sorted array
  const lowestTierPrice = sortedTiers.length > 0 ?
    sortedTiers[sortedTiers.length - 1].price_per_line : 20;

  // Calculate savings
  const savingsPerLine = standardPrice - lowestTierPrice;
  const annualSavingsPerLine = savingsPerLine * 12;
  const totalAnnualSavings = annualSavingsPerLine * lines;

  // Calculate total value
  const totalValue = verizonPromotion + totalAnnualSavings;

  // For debugging
  console.log('Standard price:', standardPrice);
  console.log('Lowest tier price:', lowestTierPrice);
  console.log('Savings per line:', savingsPerLine);
  console.log('Annual savings total:', totalAnnualSavings);

  return (
    <div className="pd-savings-benefits">
      <h2 className="pd-section-title">Your Savings & Benefits</h2>
      <div className="pd-savings-grid" style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
        gap: '1rem',
        marginBottom: '3.5rem' /* Increased bottom margin to make room for the banner */
      }}>
        {/* Card 1: Verizon Promotion */}
        <motion.div
          className="pd-savings-item"
          whileHover={{ scale: 1.03 }}
          style={{
            borderColor: `${carrierTheme.primary}40` || 'var(--primary-accent-soft)',
            display: 'flex',
            flexDirection: 'column',
            height: '100%'
          }}
        >
          <h4 style={{
            textAlign: 'center',
            height: '50px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>Verizon Promotion</h4>
          <p className="pd-savings-amount" style={{
            color: carrierTheme.primary || 'var(--primary-accent)',
            textAlign: 'center',
            margin: '0',
            fontSize: '2.5rem',
            fontWeight: '700',
            height: '70px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            ${verizonPromotion.toFixed(2)}
          </p>
          <p className="pd-savings-details" style={{
            textAlign: 'center',
            flex: '1',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            marginTop: '1rem'
          }}>
            Special offer for new customers
          </p>
        </motion.div>

        {/* Card 2: Potential Annual Savings */}
        <motion.div
          className="pd-savings-item"
          whileHover={{ scale: 1.03 }}
          style={{
            borderColor: `${carrierTheme.primary}40` || 'var(--primary-accent-soft)',
            display: 'flex',
            flexDirection: 'column',
            height: '100%'
          }}
        >
          <h4 style={{
            textAlign: 'center',
            height: '50px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>Potential Annual Savings</h4>
          <p className="pd-savings-amount" style={{
            color: carrierTheme.primary || 'var(--primary-accent)',
            textAlign: 'center',
            margin: '0',
            fontSize: '2.5rem',
            fontWeight: '700',
            height: '70px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            ${isNaN(totalAnnualSavings) ? "0.00" : totalAnnualSavings.toFixed(2)}
          </p>
          <p className="pd-savings-details" style={{
            textAlign: 'center',
            flex: '1',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            marginTop: '1rem'
          }}>
            Pay ${isNaN(lowestTierPrice) ? "0.00" : lowestTierPrice.toFixed(2)} instead of ${isNaN(standardPrice) ? "0.00" : standardPrice.toFixed(2)}
          </p>
        </motion.div>

        {/* Card 3: Value-Added Features */}
        <motion.div
          className="pd-savings-item"
          whileHover={{ scale: 1.03 }}
          style={{
            borderColor: `${carrierTheme.primary}40` || 'var(--primary-accent-soft)',
            display: 'flex',
            flexDirection: 'column',
            height: '100%'
          }}
        >
          <h4 style={{
            textAlign: 'center',
            height: '50px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>Value-Added Features</h4>
          <ul className="pd-value-features-list" style={{
            marginTop: '0',
            flex: '1',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            paddingTop: '1rem'
          }}>
            <li>
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                background: `${carrierTheme.primary}15`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: '0.75rem',
                flexShrink: 0
              }}>
                <Wifi size={16} style={{ color: carrierTheme.primary || '#FF0044' }} />
              </div>
              <span>Priority Data & Mobile Hotspot</span>
            </li>
            <li>
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                background: `${carrierTheme.primary}15`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: '0.75rem',
                flexShrink: 0
              }}>
                <Globe size={16} style={{ color: carrierTheme.primary || '#FF0044' }} />
              </div>
              <span>International Texting & Data</span>
            </li>
          </ul>
        </motion.div>
      </div>

      {/* Total value section */}
      <motion.div
        className="pd-total-value"
        whileHover={{ y: -3, boxShadow: '0 10px 25px rgba(255, 0, 68, 0.2)' }}
        style={{
          background: carrierTheme.gradient || 'var(--primary-gradient)',
          color: '#FFFFFF',
          padding: '1rem 1.5rem',
          borderRadius: '16px',
          fontWeight: 600,
          fontSize: '1.1rem',
          textAlign: 'center',
          marginTop: '1.5rem',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Background accent effect */}
        <div style={{
          position: 'absolute',
          width: '150px',
          height: '150px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0) 70%)',
          borderRadius: '50%',
          top: '-75px',
          right: '-75px',
          pointerEvents: 'none'
        }}></div>

        <span style={{ position: 'relative', zIndex: 1 }}>
          Total value of over ${isNaN(totalValue) ? "0.00" : totalValue.toFixed(2)} in savings and benefits!
        </span>
      </motion.div>
    </div>
  );
};

const CostItem = ({ label, value, info, isTotal, fontWeight = 400, fontSize }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 1.5,
      color: 'var(--text-primary)'
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Typography sx={{ fontWeight, color: 'inherit' }}>
        {label}
      </Typography>
      {info && (
        <Tooltip title={info} arrow placement="top">
          <div style={{ display: 'flex', cursor: 'help' }}>
            <Info size={16} style={{ color: 'var(--text-tertiary)' }} />
          </div>
        </Tooltip>
      )}
    </Box>
    <Typography
      sx={{
        fontWeight,
        fontSize: fontSize || 'inherit',
        color: isTotal ? 'var(--primary-accent)' : 'inherit'
      }}
    >
      ${value.toFixed(2)}
    </Typography>
  </Box>
);

const EnhancedPricing = ({
  costs,
  perLineCost,
  lines,
  onLinesChange,
  pricing,
  currentMembers,
  familySize,
  carrierTheme,
  availableSlots,
  onReserve // Added missing prop
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [animatePrice, setAnimatePrice] = useState(false);
  const isMaxLinesReached = lines >= availableSlots;

  // Reset animation state whenever price changes
  useEffect(() => {
    if (lines > 0) {
      setAnimatePrice(true);
      const timer = setTimeout(() => {
        setAnimatePrice(false);
      }, 600);
      return () => clearTimeout(timer);
    }
  }, [lines]);

  // Calculate the price user would get when joining
  const getNextTierPrice = () => {
    const tiers = pricing?.pricing_tiers || [];
    // Calculate total members using props instead of planData
    const currentTotal = currentMembers + familySize;
    const totalMembers = currentTotal + lines;

    // Sort tiers by line_count to ensure proper ordering
    const sortedTiers = [...tiers].sort((a, b) => a.line_count - b.line_count);

    // Find the appropriate tier based on total members
    const nextTier = sortedTiers.find(tier => tier.line_count >= totalMembers);

    // If no matching tier found, use the last tier (highest member count)
    return nextTier?.price_per_line || sortedTiers[sortedTiers.length - 1]?.price_per_line || perLineCost;
  };

  // Get actual price per line
  const actualPerLinePrice = getNextTierPrice();

  // Recalculate costs with new price
  const updatedCosts = {
    carrierMobileCost: actualPerLinePrice * lines,
    monthlyCarrierCost: (actualPerLinePrice * lines) + (5 * lines),
    totalActivationCost: 35 * lines,
    estimatedTaxes: 5 * lines,
    carrierTodayPayment: (actualPerLinePrice * lines) + (35 * lines) + (5 * lines),
    totalCircledinCost: costs.totalCircledinCost,
    totalTodayPayment: (actualPerLinePrice * lines) + (35 * lines) + (5 * lines) + costs.totalCircledinCost
    
  };

  return (
    <div className="pd-pricing">
      <Typography
        variant="h4"
        className="pd-section-title"
        sx={{
          fontSize: '1.5rem',
          fontWeight: 600,
          marginBottom: '1.5rem',
          color: 'var(--text-primary)'
        }}
      >
        Your Plan Breakdown
      </Typography>

      <Box
        className="pd-line-adjuster"
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '1.5rem 0',
          borderBottom: '1px solid',
          borderColor: 'var(--border-color)',
          marginBottom: '1.5rem'
        }}
      >
        <Typography
          variant="body1"
          sx={{
            fontSize: '1rem',
            fontWeight: 500,
            color: 'var(--text-primary)'
          }}
        >
          Number of lines
        </Typography>
        <div className="pd-line-adjuster-controls">
          <motion.button
            className="pd-line-adjuster-button"
            onClick={() => onLinesChange(Math.max(1, lines - 1))}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            disabled={lines <= 1}
            style={{
              background: carrierTheme.gradient,
              opacity: lines <= 1 ? 0.5 : 1
            }}
          >
            <Minus size={20} />
          </motion.button>
          <Typography
            variant="body1"
            sx={{
              margin: '0 1.25rem',
              fontSize: '1.125rem',
              fontWeight: 600,
              color: 'var(--text-primary)'
            }}
          >
            {lines}
          </Typography>
          <motion.button
            className="pd-line-adjuster-button"
            onClick={() => onLinesChange(Math.min(availableSlots, lines + 1))}
            whileHover={{ scale: isMaxLinesReached ? 1.0 : 1.1 }}
            whileTap={{ scale: isMaxLinesReached ? 1.0 : 0.9 }}
            disabled={isMaxLinesReached}
            style={{
              background: carrierTheme.gradient,
              opacity: isMaxLinesReached ? 0.5 : 1
            }}
          >
            <Plus size={20} />
          </motion.button>
        </div>
      </Box>
      {isMaxLinesReached && (
        <Typography
          variant="caption"
          sx={{
            color: 'var(--error-color)',
            display: 'block',
            textAlign: 'center',
            mt: 1
          }}
        >
          Maximum available spots reached
        </Typography>
      )}

      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', mb: 3 }}>
        <motion.div
          key={actualPerLinePrice}
          initial={{ opacity: 0, y: -20 }}
          animate={{
            opacity: 1,
            y: 0,
            scale: animatePrice ? 1.1 : 1
          }}
          transition={{ duration: 0.3 }}
          style={{ display: 'flex', alignItems: 'baseline' }}
        >
          <Typography
            component="span"
            sx={{
              fontSize: '48px',
              fontWeight: 700,
              color: carrierTheme.primary,
              lineHeight: 1
            }}
          >
            ${actualPerLinePrice}
          </Typography>
          <Typography
            component="span"
            sx={{
              ml: 1,
              color: 'var(--text-secondary)'
            }}
          >
            / per line per month
          </Typography>
        </motion.div>
      </Box>

      <Box sx={{ mt: 3 }}>
        <CostItem
          label={lines > 1 ? `Mobile Cost (${lines} lines)` : "Mobile Cost"}
          value={updatedCosts.carrierMobileCost}
          info="Monthly charge for your mobile service"
          fontWeight={400}
        />
        <CostItem
          label="Est. Taxes"
          value={updatedCosts.estimatedTaxes}
          info="Estimated monthly taxes and fees"
          fontWeight={400}
        />
      </Box>

      <Box className="pd-monthly-total">
        <CostItem
          label="Estimated Monthly Total"
          value={updatedCosts.monthlyCarrierCost}
          isTotal={true}
          info="Total monthly charges after the first bill"
          fontWeight={600}
        />
      </Box>

      {/* UPDATED: Animated Accent Underline Design for "WHAT YOU PAY TODAY" */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          mt: 3,
          mb: 2,
          display: 'flex',
          justifyContent: 'center'
        }}
      >
        <motion.button
          onClick={() => setIsExpanded(!isExpanded)}
          className="pd-what-you-pay-button"
          whileHover={{ letterSpacing: '0.5px' }}
          whileTap={{ scale: 0.98 }}
          style={{
            background: 'none',
            border: 'none',
            padding: '12px 0',
            cursor: 'pointer',
            position: 'relative',
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          {/* Base line that spans full width */}
          <div style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '1px',
            background: 'var(--border-color)'
          }} />

          {/* Animated accent underline */}
          <motion.div
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              height: '2px',
              background: carrierTheme.primary || 'var(--primary-accent)'
            }}
            animate={{
              width: isExpanded ? '100%' : '40px'
            }}
            transition={{ duration: 0.4 }}
          />

          <span style={{
            fontWeight: 700,
            fontSize: '1rem',
            color: isExpanded ? (carrierTheme.primary || 'var(--primary-accent)') : 'var(--text-primary)',
            transition: 'color 0.3s ease'
          }}>
            WHAT YOU PAY TODAY
          </span>

          <motion.div
            animate={{
              y: isExpanded ? 3 : 0,
              rotate: isExpanded ? 180 : 0
            }}
            transition={{ duration: 0.3 }}
          >
            {isExpanded ? (
              <ChevronUp
                size={20}
                color={isExpanded ? carrierTheme.primary || 'var(--primary-accent)' : 'var(--text-primary)'}
              />
            ) : (
              <ChevronDown
                size={20}
                color={isExpanded ? carrierTheme.primary || 'var(--primary-accent)' : 'var(--text-primary)'}
              />
            )}
          </motion.div>
        </motion.button>
      </Box>

      <Collapse in={isExpanded}>
        <Box
          className="pd-today-payment"
          sx={{
            backgroundColor: `${carrierTheme.primary}10`,
            borderRadius: '12px',
            padding: '24px',
            marginTop: '16px',
            color: 'var(--text-primary)'
          }}
        >
          <Typography
            variant="h6"
            className="pd-section-subtitle" // Add a class that matches other section titles
            sx={{
              textAlign: 'center',
              marginBottom: '24px',
              fontWeight: 700,
              color: 'var(--text-primary) !important' // Use the same CSS variable used by "Your Plan Breakdown"
            }}
          >
            Carrier Charges
          </Typography>

          <Box sx={{ mb: 4 }}>
            <CostItem
              label={lines > 1 ? `Mobile Cost (${lines} lines)` : "Mobile Cost"}
              value={updatedCosts.carrierMobileCost}
              info="First month's charge for your mobile service"
              fontWeight={400}
            />
            <CostItem
              label={lines > 1 ? `Activation Fee (One-time, ${lines} lines)` : "Activation Fee (One-time)"}
              value={updatedCosts.totalActivationCost}
              info="One-time fee to activate your new line(s)"
              fontWeight={400}
            />
            <CostItem
              label="Est. Taxes"
              value={updatedCosts.estimatedTaxes}
              info="Estimated taxes and fees for your first month of service"
              fontWeight={400}
            />
            <Box sx={{
              borderTop: `1px solid ${carrierTheme.primary}40`,
              pt: 2,
              mt: 2
            }}>
              <CostItem
                label={lines > 1 ? `Carrier Total (${lines} lines)` : "Carrier Total"}
                value={updatedCosts.carrierTodayPayment}
                isTotal={true}
                fontWeight={600}
              />
            </Box>
          </Box>

          <Typography
            variant="h6"
            className="pd-section-subtitle" // Add a class that matches other section titles
            sx={{
              textAlign: 'center',
              marginBottom: '24px',
              fontWeight: 700,
              color: 'var(--text-primary) !important' // Use the same CSS variable used by "Your Plan Breakdown"
            }}
          >
            Circledin Charges
          </Typography>

          <Box sx={{ mb: 4 }}>
            <div style={{
              backgroundColor: 'var(--card-bg)',
              border: `1px solid ${carrierTheme.primary}40`,
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '16px'
            }}>
              <div style={{
                marginBottom: '12px',
                paddingBottom: '12px',
                borderBottom: `1px solid ${carrierTheme.primary}10`
              }}>
                <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>Selected Plan</div>
                <div style={{
                  color: carrierTheme.primary,
                  fontSize: '18px',
                  fontWeight: '600',
                  marginTop: '4px'
                }}>
                  $12 monthly • 3-Month Plan
                </div>
              </div>
              <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                Switch to annual plan at checkout to save 33% ($8/mo)
              </div>
            </div>
            <Box sx={{
              borderTop: `1px solid ${carrierTheme.primary}40`,
              pt: 2,
              mt: 2
            }}>
              <CostItem
                label={lines > 1 ? `Circledin Total (${lines} lines)` : "Circledin Total"}
                value={updatedCosts.totalCircledinCost}
                isTotal={true}
                fontWeight={600}
              />
              <div style={{ fontSize: '14px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                3-month plan selected
              </div>
            </Box>
          </Box>

          <Box sx={{
            backgroundColor: `${carrierTheme.primary}20`,
            borderRadius: '8px',
            padding: '16px',
            mt: 4
          }}>
            <CostItem
              label="Total Due Now"
              value={updatedCosts.totalTodayPayment}
              isTotal={true}
              fontWeight={700}
              fontSize="1.25rem"
            />
          </Box>

          <Typography
            variant="body2"
            className="pd-payment-note"
            sx={{
              mt: 2,
              color: 'var(--text-secondary)',
              fontSize: '0.875rem',
              textAlign: 'center'
            }}
          >
            Includes first month's charges, one-time activation fee, and full Circledin subscription
          </Typography>
        </Box>
      </Collapse>

      {/* Updated Reserve Button - Secondary style */}
      <motion.button
        className="pd-reserve-button pd-reserve-button-secondary"
        whileHover={{ scale: 1.02, boxShadow: '0 5px 15px rgba(255, 0, 68, 0.2)' }}
        whileTap={{ scale: 0.98 }}
        onClick={onReserve} // Fixed: now using the prop directly
        style={{
          background: 'var(--glass-bg)',
          color: carrierTheme.primary,
          border: `2px solid ${carrierTheme.primary}`
        }}
      >
        RESERVE YOUR PLAN
      </motion.button>
    </div>
  );
};

// ENHANCED RESERVATION MODAL WITH IMPROVED UI DESIGN
const EnhancedModal = ({ isOpen, onClose, onSelect, carrierTheme }) => {
  const [hoveredOption, setHoveredOption] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);

  const handleSelect = (optionId) => {
    setSelectedOption(optionId);
    setTimeout(() => onSelect(optionId), 400);
  };

  const options = [
    {
      id: 'newnumber',
      title: 'Get a new number',
      description: 'Choose a new phone number for your plan',
      icon: Smartphone,
      bgGradient: carrierTheme?.gradient || 'linear-gradient(135deg, #FF0044 0%, #FF5E94 100%)'
    },
    {
      id: 'existing-customer',
      title: 'Existing Verizon customer',
      description: 'Update or change your current Verizon plan',
      icon: RefreshCw,
      bgGradient: 'linear-gradient(135deg, #0057B8 0%, #00A9FF 100%)'
    },
    {
      id: 'switching-carriers',
      title: 'Switching carriers',
      description: 'Transfer your number from another carrier',
      icon: SwitchCamera,
      bgGradient: 'linear-gradient(135deg, #6366F1 0%, #A5B4FC 100%)'
    }
  ];

  // Modal backdrop variants for animation
  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.3 }
    },
    exit: {
      opacity: 0,
      transition: { duration: 0.3 }
    }
  };

  // Modal content variants for animation
  const modalVariants = {
    hidden: {
      opacity: 0,
      scale: 0.9,
      y: 20
    },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 25,
        duration: 0.4
      }
    },
    exit: {
      opacity: 0,
      scale: 0.95,
      y: -10,
      transition: {
        duration: 0.2
      }
    }
  };

  // Option item variants for staggered animation
  const optionVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: (custom) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: 0.1 + custom * 0.1,
        duration: 0.5,
        type: "spring",
        stiffness: 300,
        damping: 24
      }
    })
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="modal-backdrop"
          variants={backdropVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            backdropFilter: 'blur(8px)',
            zIndex: 1000
          }}
        >
          <motion.div
            className="modal-content"
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            style={{
              width: '550px',
              maxWidth: '90%',
              borderRadius: '24px',
              backgroundColor: 'rgba(30, 41, 59, 0.95)',
              boxShadow: '0 20px 50px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.1)',
              padding: '32px',
              overflow: 'hidden',
              position: 'relative'
            }}
          >
            {/* Background elements for visual interest */}
            <div
              style={{
                position: 'absolute',
                top: '-80px',
                right: '-80px',
                width: '200px',
                height: '200px',
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(255, 0, 68, 0.15), transparent 70%)',
                zIndex: 0
              }}
            />

            <div
              style={{
                position: 'absolute',
                bottom: '-100px',
                left: '-100px',
                width: '250px',
                height: '250px',
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(99, 102, 241, 0.1), transparent 70%)',
                zIndex: 0
              }}
            />

            {/* Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '28px',
              position: 'relative',
              zIndex: 1
            }}>
              <motion.h2
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
                style={{
                  margin: 0,
                  fontSize: '28px',
                  fontWeight: '700',
                  background: 'linear-gradient(135deg, #f8fafc, #cbd5e1)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  textShadow: '0 2px 10px rgba(0, 0, 0, 0.1)'
                }}
              >
                How would you like to proceed?
              </motion.h2>

              <motion.button
                whileHover={{ scale: 1.1, rotate: 90 }}
                whileTap={{ scale: 0.9 }}
                onClick={onClose}
                style={{
                  background: 'transparent',
                  border: 'none',
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: '#94a3b8',
                  transition: 'all 0.2s ease'
                }}
              >
                <X size={24} />
              </motion.button>
            </div>

            {/* Options */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              position: 'relative',
              zIndex: 1
            }}>
              {options.map((option, index) => (
                <motion.div
                  key={option.id}
                  custom={index}
                  variants={optionVariants}
                  initial="hidden"
                  animate="visible"
                  whileHover={{
                    scale: 1.02,
                    boxShadow: '0 15px 30px rgba(0, 0, 0, 0.2)',
                    y: -4
                  }}
                  onHoverStart={() => setHoveredOption(option.id)}
                  onHoverEnd={() => setHoveredOption(null)}
                  onClick={() => handleSelect(option.id)}
                  style={{
                    position: 'relative',
                    padding: '24px',
                    borderRadius: '16px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '20px',
                    background: option.primary ? option.bgGradient : 'rgba(51, 65, 85, 0.4)',
                    border: '1px solid',
                    borderColor: option.primary ? 'rgba(255, 255, 255, 0.15)' : 'rgba(255, 255, 255, 0.05)',
                    boxShadow: option.primary ?
                      '0 10px 25px rgba(255, 0, 68, 0.25)' :
                      '0 5px 15px rgba(0, 0, 0, 0.1)',
                    overflow: 'hidden',
                    transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)'
                  }}
                >
                  {/* Option background overlay for hover effect */}
                  {!option.primary && (
                    <motion.div
                      style={{
                        position: 'absolute',
                        inset: 0,
                        background: option.bgGradient,
                        opacity: 0,
                        zIndex: 0
                      }}
                      animate={{
                        opacity: hoveredOption === option.id ? 0.75 : 0
                      }}
                      transition={{ duration: 0.3 }}
                    />
                  )}

                  {/* Animated selection check mark */}
                  {selectedOption === option.id && (
                    <motion.div
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ type: "spring", stiffness: 300, damping: 20 }}
                      style={{
                        position: 'absolute',
                        top: 16,
                        right: 16,
                        background: 'rgba(255, 255, 255, 0.25)',
                        borderRadius: '50%',
                        width: 28,
                        height: 28,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        zIndex: 2
                      }}
                    >
                      <CheckCircle size={18} />
                    </motion.div>
                  )}

                  {/* Icon */}
                  <div style={{
                    width: '60px',
                    height: '60px',
                    borderRadius: '16px',
                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    boxShadow: '0 5px 15px rgba(0, 0, 0, 0.1)',
                    position: 'relative',
                    zIndex: 1
                  }}>
                    <option.icon
                      size={28}
                      color="#ffffff"
                    />
                  </div>

                  {/* Text content */}
                  <div style={{
                    flex: 1,
                    position: 'relative',
                    zIndex: 1
                  }}>
                    <h3 style={{
                      margin: 0,
                      marginBottom: '6px',
                      fontSize: '20px',
                      fontWeight: '600',
                      color: '#ffffff'
                    }}>
                      {option.title}
                    </h3>
                    <p style={{
                      margin: 0,
                      fontSize: '15px',
                      color: 'rgba(255, 255, 255, 0.8)',
                      lineHeight: 1.4
                    }}>
                      {option.description}
                    </p>
                  </div>

                  {/* Right arrow indicator */}
                  <motion.div
                    animate={{
                      x: hoveredOption === option.id ? 0 : -5,
                      opacity: hoveredOption === option.id ? 1 : 0.4
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      color: '#ffffff',
                      position: 'relative',
                      zIndex: 1
                    }}
                  >
                    <ArrowRight size={24} />
                  </motion.div>
                </motion.div>
              ))}
            </div>

            {/* Security note */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.8 }}
              transition={{ delay: 0.7, duration: 0.5 }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginTop: '24px',
                color: '#94a3b8',
                fontSize: '14px',
                justifyContent: 'center'
              }}
            >
              <span>Your information is secure and encrypted</span>
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const PlanDetailSkeleton = () => (
  <div className="pd-container">
    {/* Circle Info Skeleton */}
    <Box className="pd-section">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Skeleton variant="text" width={200} height={32} sx={{ bgcolor: 'var(--skeleton-color)' }} />
          <Skeleton variant="text" width={150} height={24} sx={{ bgcolor: 'var(--skeleton-color)' }} />
        </Box>
        <Skeleton variant="rectangular" width={120} height={40} sx={{ bgcolor: 'var(--skeleton-color)', borderRadius: '8px' }} />
      </Box>

      {/* Progress Bar Skeleton */}
      <Box sx={{ my: 4 }}>
        <Skeleton variant="rectangular" height={80} sx={{ bgcolor: 'var(--skeleton-color)', borderRadius: '8px' }} />
        <Box sx={{ mt: 2 }}>
          <Skeleton variant="text" width="60%" height={24} sx={{ bgcolor: 'var(--skeleton-color)' }} />
        </Box>
      </Box>
    </Box>

    {/* Unified Plan View Skeleton */}
    <Box className="pd-section">
      <Skeleton variant="text" width={250} height={40} sx={{ mb: 2, bgcolor: 'var(--skeleton-color)' }} />
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
        <Box>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rectangular" height={70} sx={{ mb: 2, borderRadius: '12px', bgcolor: 'var(--skeleton-color)' }} />
          ))}
        </Box>
        <Box>
          <Skeleton variant="text" width={150} height={24} sx={{ mb: 3, bgcolor: 'var(--skeleton-color)' }} />
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} variant="rectangular" height={100} sx={{ borderRadius: '12px', bgcolor: 'var(--skeleton-color)' }} />
            ))}
          </Box>
        </Box>
      </Box>
    </Box>

    {/* Plan Toggle & Details Skeleton */}
    <div className="pd-main-content">
      <div className="pd-left-column">
        <Box className="pd-section">
          <Skeleton variant="text" width={250} height={32} sx={{ mb: 3, bgcolor: 'var(--skeleton-color)' }} />
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 2
          }}>
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Skeleton variant="circular" width={32} height={32} sx={{ bgcolor: 'var(--skeleton-color)' }} />
                <Skeleton variant="text" width={150} sx={{ bgcolor: 'var(--skeleton-color)' }} />
              </Box>
            ))}
          </Box>
        </Box>
      </div>

      <div className="pd-right-column">
        <Box className="pd-section">
          <Skeleton variant="text" width={200} height={32} sx={{ mb: 3, bgcolor: 'var(--skeleton-color)' }} />
          <Skeleton variant="text" width={150} height={48} sx={{ mb: 2, bgcolor: 'var(--skeleton-color)' }} />

          {/* Price Breakdown */}
          {[1, 2, 3].map((i) => (
            <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', my: 2 }}>
              <Skeleton variant="text" width={120} sx={{ bgcolor: 'var(--skeleton-color)' }} />
              <Skeleton variant="text" width={80} sx={{ bgcolor: 'var(--skeleton-color)' }} />
            </Box>
          ))}

          <Skeleton
            variant="rectangular"
            height={50}
            sx={{
              mt: 3,
              borderRadius: '12px',
              bgcolor: 'var(--skeleton-color)'
            }}
          />
        </Box>
      </div>
    </div>
  </div>
);

// Main PlanDetail component
const PlanDetail = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { getAccessTokenSilently, isAuthenticated, isLoading: authIsLoading } = useAuth0(); // Added Auth0 hook
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [planData, setPlanData] = useState(null);
  const [lines, setLines] = useState(1);
  const [isReserveModalOpen, setIsReserveModalOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Track if initial data load is complete
  const initialLoadCompleteRef = useRef(false);
  // Track user selection with a ref for race condition protection
  const userSelectionRef = useRef(null);
  // Track API call count for debugging
  const apiCallCountRef = useRef(0);

  // saw_number should fire once per mount, not on every currentPlan change
  const sawNumberFiredRef = useRef(false);
  useEffect(() => {
    // A new circle id on the same mounted component is a new saw_number step.
    sawNumberFiredRef.current = false;
  }, [id]);

  // Derived value from planData
  const currentPlan = planData?.selected_plan;

  // Handle window resize
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch plan details
  useEffect(() => {
    // Wait for Auth0 to resolve isAuthenticated before fetching, otherwise a
    // logged-in visitor fetches once as logged-out (false) then again once
    // Auth0 resolves (true), flashing the skeleton.
    if (authIsLoading) {
      return;
    }

    const fetchPlanDetails = async () => {
      if (!id) {
        setError('No circle ID provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        apiCallCountRef.current += 1;
        const callNumber = apiCallCountRef.current;

        if (DEBUG_MODE) {
          console.log(`API Call #${callNumber}: Fetching plan details...`);
        }

        // This page is public (Fix-first #1): only fetch an access token when
        // there is a logged-in session. A logged-out visitor still gets the
        // circle's plan and price.
        let token;
        if (isAuthenticated) {
          token = await getAccessTokenSilently();
        }

        // Include the token in request headers only when we have one
        const response = await axios.get(
          `/api/circles/${id}/plan-details/`,
          token ? { headers: { Authorization: `Bearer ${token}` } } : {}
        );

        if (DEBUG_MODE) {
          console.log(`API Call #${callNumber}: Received response with plan: ${response.data.selected_plan?.name}`);
        }

        // IMPORTANT: Check the user selection at the time we're about to update state
        // This ensures we don't lose selections even with race conditions
        const currentUserSelection = userSelectionRef.current;

        if (currentUserSelection) {
          if (DEBUG_MODE) {
            console.log(`API Call #${callNumber}: Found user selection: ${currentUserSelection}`);
          }

          const selectedPlan = response.data.available_plans.find(
            p => p.name === currentUserSelection
          );

          if (selectedPlan) {
            // Override the API response with user's choice
            response.data.selected_plan = selectedPlan;

            if (DEBUG_MODE) {
              console.log(`API Call #${callNumber}: Overriding with user selection: ${selectedPlan.name}`);
            }
          }
        } else if (DEBUG_MODE) {
          console.log(`API Call #${callNumber}: No user selection found`);
        }

        // If this is the initial load, mark it as complete
        if (!initialLoadCompleteRef.current) {
          initialLoadCompleteRef.current = true;
          if (DEBUG_MODE) {
            console.log("Initial data load complete");
          }
        }

        setPlanData(response.data);
      } catch (err) {
        console.error('Error fetching plan details:', err);
        setError('Failed to load plan details. Please try again later.');
      } finally {
        setTimeout(() => {
          setLoading(false);
        }, 300);
      }
    };

    fetchPlanDetails();
  }, [id, getAccessTokenSilently, isAuthenticated, authIsLoading]); // Added getAccessTokenSilently to dependencies

  const handleReserve = () => {
    trackLead(planData?.circle?.id ?? id);
    setIsReserveModalOpen(true);
  };

  const handleReserveOptionSelect = (optionId) => {
    setIsReserveModalOpen(false);
    
    // Map option IDs to joining condition values
    let joiningCondition = "";
    if (optionId === "newnumber") {
      joiningCondition = "new_number";
    } else if (optionId === "existing-customer") {
      joiningCondition = "existing_customer";
    } else if (optionId === "switching-carriers") {
      joiningCondition = "switching_carriers";
    }
    
    navigate(`/${optionId}`, {
      state: {
        planData,
        selected_plan: currentPlan,
        lines: lines,
        joining_condition: joiningCondition // Add this
      }
    });
  };

  // UPDATED: Update handlePlanChange to ensure it's protected from race conditions and blocks T-Mobile plan changes
  const handlePlanChange = (planName) => {
    // Check if carrier allows mix-and-match plans
    if (planData?.carrier?.name === 'T-Mobile') {
      if (DEBUG_MODE) {
        console.log('Plan change blocked: T-Mobile does not allow mix-and-match plans');
      }
      return; // Exit early for T-Mobile
    }

    if (DEBUG_MODE) {
      console.log(`User selected plan: ${planName}`);
    }

    // Store the selection for future reference
    userSelectionRef.current = planName;

    const newPlan = planData.available_plans.find(p => p.name === planName);
    if (newPlan) {
      setPlanData(prevData => ({
        ...prevData,
        selected_plan: newPlan
      }));
    }
  };

  const calculateCosts = () => {
    const standardPrice = currentPlan?.pricing?.pricing_tiers?.[0]?.price_per_line || 70;
    const totalMembers = (planData.circle.family_size || 0) + lines;
    const tiers = currentPlan?.pricing?.pricing_tiers || [];
    const sortedTiers = [...tiers].sort((a, b) => a.line_count - b.line_count);
    const nextTier = sortedTiers.find(tier => tier.line_count >= totalMembers);
    const groupPrice = nextTier?.price_per_line || currentPlan?.per_line_cost || 45;

    const carrierMobileCost = groupPrice * lines;
    const standardPlanCost = standardPrice * lines;
    const estimatedTaxes = 5 * lines;
    const monthlyCarrierCost = carrierMobileCost + estimatedTaxes;
    const instantSavings = standardPlanCost - carrierMobileCost;
    const annualSavings = instantSavings * 12;

    const activationFee = 35;
    const circledinCost = 36;
    const monthlyCircledinCost = 12 * lines;
    const totalCircledinCost = circledinCost * lines;

    const carrierTodayPayment = monthlyCarrierCost + (activationFee * lines);
    const totalTodayPayment = carrierTodayPayment + totalCircledinCost;

    return {
      carrierMobileCost,
      estimatedTaxes,
      totalActivationCost: activationFee * lines,
      monthlyCarrierCost,
      monthlyCircledinCost,
      totalCircledinCost,
      totalMonthlyCost: monthlyCarrierCost + monthlyCircledinCost,
      carrierTodayPayment,
      totalTodayPayment,
      instantSavings,
      annualSavings,
      standardPlanCost
    };
  };

  // Log current plan changes for debugging
  useEffect(() => {
    if (DEBUG_MODE && currentPlan) {
      console.log("Current plan is now:", currentPlan.name);
    }
  }, [currentPlan]);

  // saw_number: the computed monthly price first renders for this visitor.
  // Fire once per mount, not on every currentPlan change.
  useEffect(() => {
    if (currentPlan && !sawNumberFiredRef.current) {
      sawNumberFiredRef.current = true;
      trackSawNumber(planData?.carrier?.name, calculateCosts().totalMonthlyCost);
    }
  }, [currentPlan]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return <PlanDetailSkeleton />;
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!planData) {
    return (
      <Box p={3}>
        <Alert severity="warning">No plan data available</Alert>
      </Box>
    );
  }

  const carrierTheme = carrierThemes[planData.carrier.name] || carrierThemes['Verizon'];

  const circleDataForSharing = {
    id: planData.circle.id,
    name: planData.circle.name,
    carrier_name: planData.carrier.name,
    monthly_payment_per_line: currentPlan.pricing?.current_tier?.price_per_line || currentPlan.per_line_cost,
    available_slots: planData.circle.available_slots,
    plan_name: currentPlan.name,
    features: currentPlan.features,
    isVerified: planData.circle.status === 'Verified',
    currentMembers: planData.circle.total_joiners,
    maxMembers: planData.circle.family_size
  };

  return (
    <ThemeProvider>
      <CircleMetaTags circle={circleDataForSharing} />
      <div className="pd-container">
        <section className="pd-section pd-section-circle-info">
          <CircleInfo
            circle={planData.circle}
            carrier={planData.carrier}
            plan={currentPlan}
            shareButton={<ShareCircleButton circle={circleDataForSharing} />}
            carrierTheme={carrierTheme}
          />
        </section>

        {/* UPDATED: Unified Plan View with carrier prop */}
        <section className="pd-section pd-section-unified">
          <UnifiedPlanView
            plans={planData.available_plans}
            activePlan={currentPlan.name}
            onPlanChange={handlePlanChange}
            features={currentPlan.features}
            carrierTheme={carrierTheme}
            carrier={planData.carrier} // Add carrier prop
          />
        </section>

        <div className="pd-main-content">
          <div className="pd-left-column">
            <section className="pd-section pd-section-plan-offers">
              <WhatThisPlanOffers
                circle={{
                  shared_perks: Boolean(planData?.circle?.shared_perks),
                  additional_discount: Boolean(planData?.circle?.additional_discount),
                  activation_fee_waived: Boolean(planData?.circle?.activation_fee_waived),
                  allow_phone_upgrade: Boolean(planData?.circle?.allow_phone_upgrade)
                }}
                carrierTheme={carrierTheme}
              />
            </section>

            <section className="pd-section pd-section-savings">
              <SavingsAndBenefits
                costs={calculateCosts()}
                carrierTheme={carrierTheme}
                currentPlan={currentPlan}
                lines={lines}
              />
            </section>
          </div>

          <div className="pd-right-column">
            <section className="pd-section pd-section-pricing pd-glassmorphism">
              <EnhancedPricing
                costs={calculateCosts()}
                perLineCost={currentPlan.pricing?.current_tier?.price_per_line || currentPlan.per_line_cost}
                lines={lines}
                onLinesChange={setLines}
                pricing={currentPlan.pricing}
                currentMembers={planData.circle.total_joiners}
                familySize={planData.circle.family_size}
                carrierTheme={carrierTheme}
                availableSlots={planData.circle.available_slots}
                onReserve={handleReserve} /* Pass the handleReserve function as a prop */
              />
            </section>

            {/* Bottom trust indicators */}
            <section className="pd-section pd-section-trust pd-glassmorphism">
              <div className="pd-trust-indicators">
                <motion.div className="pd-trust-card"
                  whileHover={{ scale: 1.05 }}>
                  <h4>Customer Satisfaction</h4>
                  <div className="pd-trust-stars">
                    {'★★★★★'.split('').map((star, i) => (
                      <span key={i}>{star}</span>
                    ))}
                  </div>
                  <div className="pd-trust-value">90%</div>

                </motion.div>

                <motion.div className="pd-trust-card pd-trust-card-list"
                  whileHover={{ scale: 1.05 }}>
                  <h4>Plan Flexibility</h4>
                  <ul>
                    <li><Check size={16} /> No Credit Checks</li>
                    <li><Check size={16} /> No Annual Commitments</li>
                    <li><Check size={16} /> Switch Plans Seamlessly</li>
                  </ul>
                </motion.div>
              </div>
            </section>
          </div>
        </div>

        <EnhancedModal
          isOpen={isReserveModalOpen}
          onClose={() => setIsReserveModalOpen(false)}
          onSelect={handleReserveOptionSelect}
          carrierTheme={carrierTheme}
        />

        {/* Add Enhanced Spatial Context Action Plane with line adjustment */}
        {!isReserveModalOpen && !loading && !error && planData && (
          <SpatialContextActionPlane
            planName={currentPlan?.name || ""}
            pricePerLine={currentPlan?.pricing?.current_tier?.price_per_line || currentPlan?.per_line_cost || 0}
            activePlanFeatures={currentPlan?.features?.length || 4}
            totalFeatures={8}
            carrierTheme={carrierTheme}
            onReserve={handleReserve}
            lines={lines}
            onLinesChange={setLines}
            availableSlots={planData.circle.available_slots}
            pricing={currentPlan.pricing}
            currentMembers={planData.circle.total_joiners} // Added this prop
            familySize={planData.circle.family_size}       // Added this prop
          />
        )}
      </div>
    </ThemeProvider>
  );
};

export default PlanDetail;